import { Camera, ShieldCheck, Fingerprint } from "lucide-react";
import { StoreBadges } from "@/components/StoreBadges";

const PhoneMockup = () => (
  <div
    data-testid="hero-phone-mockup"
    className="relative w-[260px] h-[540px] rounded-[2.5rem] border-[10px] border-[#2C3B5E] bg-[#0E1730] shadow-2xl overflow-hidden shrink-0"
  >
    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-28 h-6 bg-[#2C3B5E] rounded-b-2xl" />
    <div className="p-4 pt-10 flex flex-col h-full">
      <div className="flex items-center justify-between text-[#F7F6F2]/70 text-[10px] font-semibold tracking-wider">
        <span>MOVE-IN · UNIT 4B</span>
        <span className="text-[#C9A227]">OFFLINE OK</span>
      </div>
      <p className="text-[#F7F6F2] font-heading font-bold mt-3 text-sm">Kitchen — 4 of 12 areas</p>
      <div className="mt-3 flex-1 rounded-xl bg-[#2C3B5E]/40 border border-white/10 flex items-center justify-center relative">
        <Camera className="w-10 h-10 text-[#F7F6F2]/40" strokeWidth={1.5} />
        <span className="absolute bottom-3 left-3 right-3 text-center text-[9px] text-[#F7F6F2]/50 tracking-wide">
          Your app screenshot goes here
        </span>
      </div>
      <div className="mt-3 rounded-lg bg-[#2C3B5E]/60 px-3 py-2 flex items-center gap-2">
        <Fingerprint className="w-4 h-4 text-[#C9A227]" />
        <span className="text-[9px] text-[#F7F6F2]/80 font-mono truncate">SHA-256 · a3f9…e21c · 14:02:11 UTC</span>
      </div>
      <div className="mt-3 mb-2 h-12 rounded-full bg-[#C9A227] flex items-center justify-center gap-2">
        <ShieldCheck className="w-5 h-5 text-[#14213D]" />
        <span className="text-[#14213D] font-heading font-bold text-sm">Capture & fingerprint</span>
      </div>
    </div>
  </div>
);

const PdfCard = () => (
  <div
    data-testid="hero-pdf-card"
    className="hidden xl:block w-[280px] bg-white rounded-lg shadow-2xl rotate-3 overflow-hidden border border-black/5 ml-6 mt-20"
  >
    <img src="/assets/veristamp_pdf_header.svg" alt="Veristamp certified condition report PDF header with report ID and SHA-256 verification line" width="300" height="41" loading="lazy" className="w-full h-auto" />
    <div className="p-4 space-y-2">
      {[92, 100, 78, 100, 64].map((w, i) => (
        <div key={i} className="h-2 rounded bg-[#5B6474]/20" style={{ width: `${w}%` }} />
      ))}
      <div className="grid grid-cols-3 gap-2 pt-2">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-14 rounded bg-[#2C3B5E]/15" />
        ))}
      </div>
      <div className="flex items-center gap-2 pt-2">
        <ShieldCheck className="w-4 h-4 text-[#C9A227]" />
        <span className="text-[10px] text-[#5B6474] font-mono">Verification page · 3 of 3 signatures</span>
      </div>
    </div>
  </div>
);

export const Hero = () => (
  <header id="download" className="relative bg-[#14213D] overflow-hidden">
    <div className="stamp-watermark" style={{ backgroundImage: "url(/assets/veristamp_icon_master.svg)" }} />
    <div className="max-w-7xl mx-auto px-6 md:px-12 pt-36 pb-24 grid lg:grid-cols-2 gap-16 items-center relative">
      <div className="fade-up">
        <div className="inline-flex items-center gap-2 border border-[#C9A227]/50 rounded-full px-4 py-1.5 mb-8">
          <span className="w-2 h-2 rounded-full bg-[#C9A227]" />
          <span className="text-[#C9A227] text-xs font-semibold tracking-widest uppercase">Timestamped. Tamper-evident.</span>
        </div>
        <h1
          data-testid="hero-headline"
          className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-[#F7F6F2] tracking-tight leading-[1.05]"
        >
          Evidence that protects <span className="text-[#C9A227]">your deposit.</span>
        </h1>
        <p data-testid="hero-subhead" className="mt-6 text-base md:text-lg text-[#F7F6F2]/75 leading-relaxed max-w-xl">
          Veristamp turns your move-in and move-out photos into timestamped, tamper-evident condition reports —
          signed by every party, verified by cryptography.
        </p>
        <div className="mt-10">
          <StoreBadges idPrefix="hero" />
          <p data-testid="hero-platform-line" className="mt-4 text-sm text-[#F7F6F2]/60">
            Available on iPhone and Android.
          </p>
        </div>
      </div>
      <div className="flex justify-center lg:justify-end fade-up fade-up-2">
        <PhoneMockup />
        <PdfCard />
      </div>
    </div>
  </header>
);
